/**
 * User APIs - sign up, log in, log out
 */

import { WNextBase } from '../api/wnext-base';
import type { SignedNostrEvent } from '../crypto/nostr';
import { encryptAukForPasskeyPRF, type PasskeyAukEnvelope } from '../crypto/passkey';
import { IncorrectLoginError } from '../pollen/errors';
import { arrayBufferToBase64URL, base64URLToArrayBuffer } from '../util/base64';

export type SignUpRequest = PasswordSignUpRequest | NostrSignUpRequest;

export interface PasswordSignUpRequest {
  authType: 'password';
  email: string;
  password: string;
  encryptedState?: string;
  salt: string;
}

export interface NostrSignUpRequest {
  authType: 'nostr';
  authEvent: SignedNostrEvent;
  encryptedAuk: string;
  email: string;
}

export interface SignInRequest {
  email: string;
  password: string;
}

export interface PasskeySignInRequest {
  email: string;
}

export interface NostrSignInRequest {
  authEvent: SignedNostrEvent;
}

export interface NostrSignInResponse {
  encryptedAuk: string;
  email: string;
}

export interface SignInResponse {
  encryptedState?: string;
  vaultEnvelope?: PasskeyAukEnvelope;
}

export interface PasskeySignInResult {
  response: SignInResponse;
  credentialID: string;
  prfOutput?: Uint8Array;
}

export interface PasskeyRegistrationOptionsRequest {
  email?: string;
  nickname?: string;
}

export interface PasskeyRegistrationOptionsResponse {
  challengeId: string;
  publicKey: PublicKeyCredentialCreationOptionsJSON;
  mediation?: CredentialMediationRequirement;
}

export interface PasskeyLoginOptionsResponse {
  challengeId: string;
  publicKey: PublicKeyCredentialRequestOptionsJSON;
  mediation?: CredentialMediationRequirement;
  rpId: string;
}

export interface PasskeyPRFEnrollOptionsResponse {
  challengeId: string;
  publicKey: PublicKeyCredentialRequestOptionsJSON;
  mediation?: CredentialMediationRequirement;
  rpId: string;
}

export interface PasskeyCredential {
  id: string;
  subject?: string;
  rpId: string;
  publicKeyCose: string;
  signCount: number;
  nickname?: string | null;
  transports: string[];
  createdAt: string;
  lastUsedAt?: string | null;
  backupEligible: boolean;
  backupState: boolean;
  vaultUnlock: boolean;
}

export interface LocalSsoInvitePasskey {
  id: string;
  rpId: string;
  publicKeyCose: string;
  signCount: number;
  transports: string[];
}

export interface LocalSsoInviteRecipient {
  subject: string;
  passkeys: LocalSsoInvitePasskey[];
}

export interface LocalSsoInvite {
  id: number;
  deploymentFqdn: string;
  createdAt: string;
  expiresAt: string;
  redeemedAt?: string;
  revokedAt?: string;
  recipient?: LocalSsoInviteRecipient;
}

export interface CreateLocalSsoInviteResponse {
  invite: LocalSsoInvite;
  token: string;
}

export interface LocalSsoAdministrator extends LocalSsoInviteRecipient {
  isOwner: boolean;
}

export interface LocalSsoAccess {
  invites: LocalSsoInvite[];
  administrators: LocalSsoAdministrator[];
}

export interface PasskeyRegistrationCredentialJSON {
  id: string;
  rawId: string;
  type: 'public-key';
  authenticatorAttachment?: string | null;
  response: {
    attestationObject: string;
    clientDataJSON: string;
    transports?: string[];
  };
  clientExtensionResults: Record<string, unknown>;
}

export interface PasskeyRegistrationResult {
  credential: PasskeyRegistrationCredentialJSON;
  prfEnabled: boolean;
}

export interface PasskeyAuthenticationCredentialJSON {
  id: string;
  rawId: string;
  type: 'public-key';
  authenticatorAttachment?: string | null;
  response: {
    authenticatorData: string;
    clientDataJSON: string;
    signature: string;
    userHandle?: string;
  };
  clientExtensionResults: Record<string, unknown>;
}

export interface AuthenticationExtensionsPRFValuesJSON {
  first: string;
  second?: string;
}

export interface AuthenticationExtensionsPRFInputsJSON {
  eval?: AuthenticationExtensionsPRFValuesJSON;
  evalByCredential?: Record<string, AuthenticationExtensionsPRFValuesJSON>;
}

export interface AuthenticationExtensionsClientInputsJSON extends Omit<
  AuthenticationExtensionsClientInputs,
  'prf'
> {
  prf?: AuthenticationExtensionsPRFInputsJSON;
}

export interface PublicKeyCredentialDescriptorJSON extends Omit<
  PublicKeyCredentialDescriptor,
  'id'
> {
  id: string;
}

export interface PublicKeyCredentialUserEntityJSON extends Omit<
  PublicKeyCredentialUserEntity,
  'id'
> {
  id: string;
}

export interface PublicKeyCredentialCreationOptionsJSON extends Omit<
  PublicKeyCredentialCreationOptions,
  'challenge' | 'excludeCredentials' | 'extensions' | 'user'
> {
  challenge: string;
  excludeCredentials?: PublicKeyCredentialDescriptorJSON[];
  extensions?: AuthenticationExtensionsClientInputsJSON;
  user: PublicKeyCredentialUserEntityJSON;
}

export interface PublicKeyCredentialRequestOptionsJSON extends Omit<
  PublicKeyCredentialRequestOptions,
  'allowCredentials' | 'challenge' | 'extensions'
> {
  allowCredentials?: PublicKeyCredentialDescriptorJSON[];
  challenge: string;
  extensions?: AuthenticationExtensionsClientInputsJSON;
}

export interface AuthChallengeRequest {
  email: string;
}

export interface AuthChallengeResponse {
  salt?: string;
}

export interface ReadStateResponse {
  encryptedState?: string;
}

export interface WriteStateRequest {
  encryptedState: string;
}

interface AcceptPublisherTermsReq {
  publisherID?: string;
  invite: string | null | undefined;
}

interface NotifyMeRequest {
  appID: string;
  appName: string;
  email: string;
}

function credentialDescriptorFromJSON(
  descriptor: PublicKeyCredentialDescriptorJSON
): PublicKeyCredentialDescriptor {
  return {
    ...descriptor,
    id: base64URLToArrayBuffer(descriptor.id),
  };
}

function prfValuesFromJSON(
  values: AuthenticationExtensionsPRFValuesJSON
): AuthenticationExtensionsPRFValues {
  return {
    first: base64URLToArrayBuffer(values.first),
    second: values.second ? base64URLToArrayBuffer(values.second) : undefined,
  };
}

function extensionsFromJSON(
  extensions?: AuthenticationExtensionsClientInputsJSON
): AuthenticationExtensionsClientInputs | undefined {
  if (!extensions) {
    return undefined;
  }
  const { prf, ...otherExtensions } = extensions;
  if (!prf) {
    return otherExtensions;
  }
  const evalByCredential = prf.evalByCredential
    ? Object.fromEntries(
        Object.entries(prf.evalByCredential).map(([credentialID, values]) => [
          credentialID,
          prfValuesFromJSON(values),
        ])
      )
    : undefined;
  return {
    ...otherExtensions,
    prf: {
      eval: prf.eval ? prfValuesFromJSON(prf.eval) : undefined,
      evalByCredential,
    },
  };
}

export function credentialCreationOptionsFromJSON(
  publicKey: PublicKeyCredentialCreationOptionsJSON
): PublicKeyCredentialCreationOptions {
  const { extensions, ...creationOptions } = publicKey;
  return {
    ...creationOptions,
    challenge: base64URLToArrayBuffer(publicKey.challenge),
    excludeCredentials: publicKey.excludeCredentials?.map(credentialDescriptorFromJSON),
    extensions: extensionsFromJSON(extensions),
    user: {
      ...publicKey.user,
      id: base64URLToArrayBuffer(publicKey.user.id),
    },
  };
}

export function credentialRequestOptionsFromJSON(
  publicKey: PublicKeyCredentialRequestOptionsJSON
): PublicKeyCredentialRequestOptions {
  const { extensions, ...requestOptions } = publicKey;
  return {
    ...requestOptions,
    allowCredentials: publicKey.allowCredentials?.map(credentialDescriptorFromJSON),
    challenge: base64URLToArrayBuffer(publicKey.challenge),
    extensions: extensionsFromJSON(extensions),
  };
}

function credentialsContainer(): CredentialsContainer {
  if (typeof navigator === 'undefined' || !navigator.credentials) {
    throw new Error('passkeys are not available in this browser');
  }
  return navigator.credentials;
}

export function passkeysAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials?.create &&
    !!navigator.credentials?.get
  );
}

function serializableClientExtensionResults(
  extensionResults: AuthenticationExtensionsClientOutputs
): Record<string, unknown> {
  const serializable = { ...extensionResults };
  delete serializable.prf;
  return serializable;
}

function prfOutputFromClientExtensionResults(
  extensionResults: AuthenticationExtensionsClientOutputs
): Uint8Array | undefined {
  const output = extensionResults.prf?.results?.first;
  if (!output) {
    return undefined;
  }
  if (output instanceof ArrayBuffer) {
    return new Uint8Array(output);
  }
  const view = output;
  return new Uint8Array(view.buffer, view.byteOffset, view.byteLength).slice();
}

export async function createPasskeyCredential(
  publicKey: PublicKeyCredentialCreationOptionsJSON
): Promise<PasskeyRegistrationResult> {
  const credential = await credentialsContainer().create({
    publicKey: credentialCreationOptionsFromJSON(publicKey),
  });
  if (!credential || credential.type !== 'public-key') {
    throw new Error('passkey registration was cancelled');
  }

  const publicKeyCredential = credential as PublicKeyCredential;
  const response = publicKeyCredential.response as AuthenticatorAttestationResponse;
  const clientExtensionResults = publicKeyCredential.getClientExtensionResults();
  return {
    credential: {
      id: publicKeyCredential.id,
      rawId: arrayBufferToBase64URL(publicKeyCredential.rawId),
      type: 'public-key',
      authenticatorAttachment: publicKeyCredential.authenticatorAttachment,
      response: {
        attestationObject: arrayBufferToBase64URL(response.attestationObject),
        clientDataJSON: arrayBufferToBase64URL(response.clientDataJSON),
        transports: response.getTransports?.(),
      },
      clientExtensionResults: serializableClientExtensionResults(clientExtensionResults),
    },
    prfEnabled: clientExtensionResults.prf?.enabled === true,
  };
}

export async function getPasskeyCredential(
  publicKey: PublicKeyCredentialRequestOptionsJSON,
  mediation?: CredentialMediationRequirement
): Promise<{ credential: PasskeyAuthenticationCredentialJSON; prfOutput?: Uint8Array }> {
  const credential = await credentialsContainer().get({
    mediation,
    publicKey: credentialRequestOptionsFromJSON(publicKey),
  });
  if (!credential || credential.type !== 'public-key') {
    throw new Error('passkey login was cancelled');
  }

  const publicKeyCredential = credential as PublicKeyCredential;
  const response = publicKeyCredential.response as AuthenticatorAssertionResponse;
  const clientExtensionResults = publicKeyCredential.getClientExtensionResults();
  return {
    credential: {
      id: publicKeyCredential.id,
      rawId: arrayBufferToBase64URL(publicKeyCredential.rawId),
      type: 'public-key',
      authenticatorAttachment: publicKeyCredential.authenticatorAttachment,
      response: {
        authenticatorData: arrayBufferToBase64URL(response.authenticatorData),
        clientDataJSON: arrayBufferToBase64URL(response.clientDataJSON),
        signature: arrayBufferToBase64URL(response.signature),
        userHandle: response.userHandle ? arrayBufferToBase64URL(response.userHandle) : undefined,
      },
      clientExtensionResults: serializableClientExtensionResults(clientExtensionResults),
    },
    prfOutput: prfOutputFromClientExtensionResults(clientExtensionResults),
  };
}

async function unwrapRawJSON<T>(res: Response | Error, expectedStatus: number): Promise<T> {
  if (res instanceof Error) {
    throw res;
  }
  if (res.status === expectedStatus) {
    return res.json();
  }
  throw new Error(res.statusText || `Request failed with status ${res.status}`);
}

export class NoPasskeyAvailableError extends Error {}

export class UserAPI extends WNextBase {
  async signUp(req: SignUpRequest): Promise<void> {
    return this.unwrap(await this.client.post('/user', req));
  }

  async signIn(req: SignInRequest): Promise<SignInResponse> {
    const res = await this.client.post('/user/signin', { authType: 'password', ...req });
    if (res instanceof Error) {
      throw res;
    }
    if (res.status === 204) {
      return {};
    }
    if (res.status === 200) {
      return res.json();
    }

    throw new IncorrectLoginError();
  }

  async signInWithNostr(req: NostrSignInRequest): Promise<NostrSignInResponse> {
    const res = await this.client.post('/user/signin', { authType: 'nostr', ...req });
    if (res instanceof Error) {
      throw res;
    }
    if (res.status === 200) {
      return res.json();
    }

    throw new IncorrectLoginError();
  }

  async beginPasskeyRegistration(
    req: PasskeyRegistrationOptionsRequest
  ): Promise<PasskeyRegistrationOptionsResponse> {
    return unwrapRawJSON(await this.client.post('/user/passkeys/register/options', req), 200);
  }

  async verifyPasskeyRegistration(
    challengeID: string,
    credential: PasskeyRegistrationCredentialJSON
  ): Promise<PasskeyCredential> {
    return unwrapRawJSON(
      await this.client.post(
        `/user/passkeys/register/verify/${encodeURIComponent(challengeID)}`,
        credential
      ),
      201
    );
  }

  async beginPasskeyPRFEnrollment(credentialID: string): Promise<PasskeyPRFEnrollOptionsResponse> {
    return unwrapRawJSON(
      await this.client.post('/user/passkeys/prf-enroll/options', { credentialId: credentialID }),
      200
    );
  }

  async verifyPasskeyPRFEnrollment(
    challengeID: string,
    credential: PasskeyAuthenticationCredentialJSON,
    vaultEnvelope: Omit<PasskeyAukEnvelope, 'rpId'>
  ): Promise<PasskeyCredential> {
    return unwrapRawJSON(
      await this.client.post(
        `/user/passkeys/prf-enroll/verify/${encodeURIComponent(challengeID)}`,
        {
          ...credential,
          vaultEnvelope,
        }
      ),
      200
    );
  }

  async enrollPasskeyPRF(credentialID: string, auk: Uint8Array): Promise<PasskeyCredential> {
    const options = await this.beginPasskeyPRFEnrollment(credentialID);
    const assertion = await getPasskeyCredential(options.publicKey, options.mediation);
    if (!assertion.prfOutput) {
      throw new Error('this passkey does not support vault unlock');
    }
    const vaultEnvelope = await encryptAukForPasskeyPRF(
      auk,
      assertion.prfOutput,
      options.rpId,
      assertion.credential.id
    );
    return this.verifyPasskeyPRFEnrollment(
      options.challengeId,
      assertion.credential,
      vaultEnvelope
    );
  }

  async registerPasskey(
    req: PasskeyRegistrationOptionsRequest,
    auk: Uint8Array
  ): Promise<PasskeyCredential> {
    const options = await this.beginPasskeyRegistration(req);
    const registration = await createPasskeyCredential(options.publicKey);
    const passkey = await this.verifyPasskeyRegistration(
      options.challengeId,
      registration.credential
    );
    if (!registration.prfEnabled) {
      return passkey;
    }
    return this.enrollPasskeyPRF(passkey.id, auk);
  }

  async beginPasskeyLogin(req: PasskeySignInRequest): Promise<PasskeyLoginOptionsResponse> {
    const response = await this.client.post('/user/passkeys/login/options', req);
    if (!(response instanceof Error) && response.status === 403) {
      throw new NoPasskeyAvailableError('No passkey available for this account.');
    }
    return unwrapRawJSON(response, 200);
  }

  async verifyPasskeyLogin(
    challengeID: string,
    credential: PasskeyAuthenticationCredentialJSON
  ): Promise<SignInResponse> {
    const res = await this.client.post(
      `/user/passkeys/login/verify/${encodeURIComponent(challengeID)}`,
      credential
    );
    if (res instanceof Error) {
      throw res;
    }
    if (res.status === 200) {
      return res.json();
    }

    throw new IncorrectLoginError();
  }

  async signInWithPasskey(req: PasskeySignInRequest): Promise<PasskeySignInResult> {
    const options = await this.beginPasskeyLogin(req);
    const assertion = await getPasskeyCredential(options.publicKey, options.mediation);
    const response = await this.verifyPasskeyLogin(options.challengeId, assertion.credential);
    return {
      response,
      credentialID: assertion.credential.id,
      prfOutput: assertion.prfOutput,
    };
  }

  async listPasskeys(): Promise<PasskeyCredential[]> {
    const res = await this.unwrapRes(this.client.get('/user/passkeys'));
    return res.data?.passkeys ?? [];
  }

  async createLocalSsoInvite(
    deploymentFqdn: string,
    library?: { appID: string; itemID: string; encItemKey: string; encItemKeyNonce: string }
  ): Promise<CreateLocalSsoInviteResponse> {
    return unwrapRawJSON(
      await this.client.post('/user/local-sso/invites', { deploymentFqdn, library }),
      201
    );
  }

  async getLocalSsoInviteLibrary(
    token: string
  ): Promise<import('../crypto/vault/api').GetShareResponse & { deploymentFqdn: string }> {
    return unwrapRawJSON(await this.client.post('/user/local-sso/invites/library', { token }), 200);
  }

  async listLocalSsoInvites(deploymentFqdn: string): Promise<LocalSsoInvite[]> {
    return (await this.getLocalSsoAccess(deploymentFqdn)).invites;
  }

  async getLocalSsoAccess(deploymentFqdn: string): Promise<LocalSsoAccess> {
    return unwrapRawJSON<LocalSsoAccess>(
      await this.client.get(
        `/user/local-sso/invites?deploymentFqdn=${encodeURIComponent(deploymentFqdn)}`
      ),
      200
    );
  }

  async redeemLocalSsoInvite(
    token: string,
    library?: { encItemKey: string; encItemKeyNonce: string }
  ): Promise<LocalSsoInvite> {
    const response = await unwrapRawJSON<{ invite: LocalSsoInvite }>(
      await this.client.post('/user/local-sso/invites/redeem', { token, library }),
      200
    );
    return response.invite;
  }

  async deleteLocalSsoInvite(inviteId: number): Promise<void> {
    await this.unwrap(await this.client.delete(`/user/local-sso/invites/${inviteId}`));
  }

  async renamePasskey(credentialID: string, nickname: string): Promise<void> {
    await this.unwrapRes(
      this.client.patch(`/user/passkeys/${encodeURIComponent(credentialID)}`, { nickname })
    );
  }

  async deletePasskey(credentialID: string): Promise<void> {
    await this.unwrapRes(this.client.delete(`/user/passkeys/${encodeURIComponent(credentialID)}`));
  }

  /**
   * Sign out forces user session to be cleared, regardless if one exists or
   * not.
   *
   * TODO: add local state/data clearing, etc.
   *
   * @returns
   */
  async signOut(): Promise<void> {
    return this.unwrap(await this.client.post('/user/signout', {}));
  }

  /**
   * Returns the auth challenge for a given user.
   */
  async authChallenge(req: AuthChallengeRequest): Promise<AuthChallengeResponse> {
    return this.unwrap(await this.client.post('/user/authinfo', req));
  }

  /**
   * Reads the latest version of encrypted state from remote storage.
   */
  async readState(email: string): Promise<ReadStateResponse> {
    const res = await this.unwrapRes(this.client.post('/user/state/read', { email }));
    return res.data;
  }

  async removeMonolithicState(): Promise<void> {
    await this.unwrapRes(this.client.delete('/user/state'));
  }

  async acceptDayPassTerms(): Promise<void> {
    await this.unwrap(await this.client.post('/user/accept-daypass-terms', {}));
  }

  async acceptPublisherTerms(req: AcceptPublisherTermsReq): Promise<void> {
    await this.unwrap(await this.client.post('/user/accept-publisher-terms', req));
  }

  async resendEmailVerification(email: string): Promise<void> {
    await this.unwrapRes(this.client.post('/user/verify-email', { email }));
  }

  async notifyMe(req: NotifyMeRequest): Promise<void> {
    await this.unwrapRes(this.client.post('/user/notify-me', req));
  }
}
